import type { SideNavElement } from './SideNavItem';
import type { FC, JSX } from 'react';

import styled, { css } from 'styled-components';

import { SideNavItem } from './SideNavItem';
import { media } from '../../core/styles';
import type { NavigationItem } from '~/utilities/graphql/codegen';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  SubMenuAnimation: styled.div`
    ${({ theme }) => css`
      box-sizing: border-box;
      overflow: hidden;

      &[data-expanded='true'] {
        max-height: 5000px;
        transition: max-height 0.5s ease-in-out, margin-top 0s cubic-bezier(0, 1, 0, 1) 0s;
        margin-top: 0;

        &[data-root='true'] {
          margin-top: -12px;
        }

        @media (prefers-reduced-motion) {
          transition: none;
        }
      }

      &[data-expanded='false'] {
        max-height: 0;
        transition: max-height 0.5s cubic-bezier(0, 1, 0, 1),
          margin-top 0s cubic-bezier(0, 1, 0, 1) 0.3s;
        margin-top: 0;

        @media (prefers-reduced-motion) {
          transition: none;
        }
      }

      @media ${media(theme).greaterThan('sm')} {
        margin-top: 0;

        &[data-expanded='true'][data-root='true'] {
          margin-top: -4px;
        }
      }

      @media (prefers-reduced-motion) {
        transition: none;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          margin-top: unset;

          &[data-expanded='true'][data-root='true'] {
            margin-top: unset;
          }

          @media ${media(theme).greaterThan('sm')} {
            margin-top: unset;
          }
        `,
      })}
    `}
  `,

  SubMenuWrapper: styled.ul`
    appearance: none;
    list-style: none;

    ${({ theme }) => css`
      padding: ${theme.padding.none} ${theme.padding.none} ${theme.padding['2xs']}
        ${theme.padding.sm};

      &[data-root='true'] {
        padding: ${theme.padding.none} ${theme.padding.none} ${theme.padding.xs}
          ${theme.padding.none};
      }

      @media ${media(theme).greaterThan('sm')} {
        padding-bottom: ${theme.padding['3xs']};

        &[data-root='true'] {
          padding-bottom: ${theme.padding.xs};
        }
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          padding: ${theme.padding.none} ${theme.padding.none} ${theme.padding.xs}
            ${theme.padding.sm};

          &[data-root='true'] {
            padding: ${theme.padding.none};
          }

          @media ${media(theme).greaterThan('sm')} {
            padding-bottom: ${theme.padding.xs};

            &[data-root='true'] {
              padding-bottom: ${theme.padding.none};
            }
          }
        `,
      })}
    `}
  `,
};

interface Props {
  activeIndexArray?: string[];
  activeItemUrl?: string | null;
  branchChildCount: number;
  children: (NavigationItem | null)[] | null;
  currentIndex: string;
  depthLevel: number;
  isExpanded: boolean;
  labelPath: string[];
  navElement: SideNavElement;
  parentLabel?: string;
  updateActiveItem: (arr: string[]) => void;
}

type SideNavItemSubMenuAnimationProps = Props & Omit<JSX.IntrinsicElements['div'], 'children'>;

export const SideNavItemSubMenuAnimation: FC<SideNavItemSubMenuAnimationProps> = ({
  activeIndexArray,
  activeItemUrl,
  branchChildCount,
  children,
  currentIndex,
  depthLevel,
  isExpanded,
  labelPath,
  navElement,
  parentLabel,
  updateActiveItem,
  ...rest
}) => (
  <S.SubMenuAnimation
    data-expanded={isExpanded}
    aria-hidden={!isExpanded}
    data-root={depthLevel === 0}
    {...rest}
  >
    <S.SubMenuWrapper data-root={depthLevel === 0}>
      {children?.map((subMenu, index) => (
        <SideNavItem
          activeIndexArray={activeIndexArray}
          activeItemUrl={activeItemUrl}
          currentIndex={`${currentIndex},${index}`}
          depthLevel={depthLevel + 1}
          isLastBranch={index === branchChildCount - 1}
          key={subMenu?.uid || index}
          labelPath={labelPath}
          navElement={navElement}
          parentActive={isExpanded}
          parentLabel={parentLabel}
          updateActiveItem={updateActiveItem}
          {...subMenu}
        />
      ))}
    </S.SubMenuWrapper>
  </S.SubMenuAnimation>
);
